import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { resetCache, setChannelCreated, setChannelDeleted, setChannelUpdated } from '../redux/actions';
import { useCollectionsByUserId } from '../api/collections';
import Spinner from '../components/Spinner';
import ChannelInfo from '../components/Channel/ChannelInfo';
import { Link } from 'react-router-dom';
import AppHeaderLayout from '../components/AppHeaderLayout';
import CreateChannelModal from '../components/Modals/CreateChannelModal';
import { buttonStyles, iconButtonStyles } from '../styles/commonStyles';
import { MegaphoneIcon } from '@heroicons/react/24/outline';
import { BiMenu } from 'react-icons/bi';

function MyChannels() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [sidebarMenuOpen, setSidebarMenuOpen] = useState(false);

    // Получаем user_id из Redux store
    const userId = useSelector(state => state.auth.user?.id);

    // Мемоизируем значение, извлекаем только нужные данные
    const { channelCreated, channelDeleted, channelUpdated } = useSelector(
        (state) => ({
            channelCreated: state.channelCreated,
            channelDeleted: state.channelDeleted,
            channelUpdated: state.channelUpdated,
        }),
        (prev, next) =>
            prev.channelCreated === next.channelCreated &&
            prev.channelDeleted === next.channelDeleted &&
            prev.channelUpdated === next.channelUpdated
    );

    const { data: channels, isLoading: isChannelsLoading, error: channelsError, refetch: refetchChannels } = useCollectionsByUserId(userId);

    // Автоматически обновляем данные при фокусе окна
    useEffect(() => {
        const handleFocus = () => {
            refetchChannels();
        };
        
        window.addEventListener('focus', handleFocus);
        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, [refetchChannels]);

    useEffect(() => {
        const shouldResetCache = channelCreated || channelDeleted || channelUpdated;

        if (shouldResetCache) {
            queryClient.invalidateQueries(['collections', userId]);
            dispatch(resetCache());
            dispatch(setChannelCreated(false));
            dispatch(setChannelDeleted(false));
            dispatch(setChannelUpdated(false));
        }
    }, [dispatch, queryClient, channelCreated, channelDeleted, channelUpdated, userId]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.code === 'Insert') {
                setIsCreateModalOpen(true);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    if (!userId) return <div>Please log in to view your channels</div>;
    if (isChannelsLoading) return <Spinner />;
    if (channelsError) return <div>Error loading channels</div>;

    const leftContent = (
        <div className="flex items-center gap-2">
            <button className={iconButtonStyles + " block sm:hidden mr-2"} onClick={() => setSidebarMenuOpen(true)} aria-label="Открыть меню">
                <BiMenu className="text-2xl" />
            </button>
            <span className="font-semibold text-base">{t('tabs.channels')}</span>
        </div>
    );
    const rightContent = (
        <button 
            onClick={() => setIsCreateModalOpen(true)} 
            className={buttonStyles}
        >
            <MegaphoneIcon className="w-5 h-5 mr-2 inline-block" />
            {t('channel.create')}
        </button>
    );

    return (
        <AppHeaderLayout
            leftContent={leftContent}
            rightContent={rightContent}
            sidebarMenuOpen={sidebarMenuOpen}
            onSidebarMenuOpen={() => setSidebarMenuOpen(true)}
            onSidebarMenuClose={() => setSidebarMenuOpen(false)}
        >
            <div className="p-4">
                <ul>
                    {channels.map((channel) => (
                        <li key={channel.id}>
                            <Link to={`/mychannels/${channel.id}`} state={{ name: channel.name, description: channel.description }}>
                                <button className="w-full flex items-center gap-4 text-left py-2 px-3">
                                    <ChannelInfo
                                        title={channel.name}
                                        subscribersCount={channel.subscribers_count}
                                        postcardsCount={channel.postcards_count}
                                    />
                                    {channel.is_default && (
                                        <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                                            Автоматический
                                        </span>
                                    )}
                                </button>
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>

            <CreateChannelModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
            />
        </AppHeaderLayout>
    );
}

export default MyChannels;
